import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Target, ArrowRight, Sparkles, CheckCircle2, MessageCircleHeart, Award, Compass, Info } from 'lucide-react';
import PIELayout from './PIELayout';
import { PIESkeletonParagraph, default as PIESkeleton } from './PIESkeleton';
import { pieApi, pieCoachPreseed } from './pieApi';
import PIEUnseededEmptyState, { isUnseededError } from './PIEUnseededEmptyState';
import PIEFallbackBadge from './PIEFallbackBadge';
import PIECalibrationBadge from './PIECalibrationBadge';
import { pp } from './format';
import { Perf } from './PIEDisplayMode';
import './pie.css';
import logger from '../../lib/logger';

/**
 * PIEImprovePage — "What Should I Improve?" — step 2 of the journey.
 *
 * Calls the AI Insight Engine. Renders:
 *   - The Coach's diagnosis (Claude, grounded in numbers)
 *   - A "Top priority" hero card with the recommended single action +
 *     a "Commit to this now" button (one click → POST /commitments → /me/validate).
 *   - A ranked list of all six action options, each with predicted ΔPE,
 *     predicted ROE, top KPI $ impact, and a one-line coach blurb.
 */
export default function PIEImprovePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [committingId, setCommittingId] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    pieApi
      .insights()
      .then(setData)
      .catch((e) => {
        logger.error('insights failed', e);
        setErr(e?.response?.data?.detail || 'Could not load insights');
      })
      .finally(() => setLoading(false));
  }, []);

  const onCommit = async (actionId) => {
    if (committingId) return;
    setCommittingId(actionId);
    try {
      await pieApi.createCommitment(actionId);
      navigate('/mirror/validate');
    } catch (e) {
      logger.error('commit failed', e);
      setErr(e?.response?.data?.detail || 'Could not save commitment');
      setCommittingId(null);
    }
  };

  /** "Talk it over" — pre-seeds the Coach with a context-aware opener
   *  built from the action the user is considering. The Coach page
   *  reads the seed on mount, auto-sends it as the user's first turn,
   *  then clears the key. Behaves identically whether the click came
   *  from the Top Priority hero card or any of the other ranked rows. */
  const onTalkOver = (option) => {
    const seed =
      `I'm thinking about committing to: "${option.label}". ` +
      `My current PE is ${(option.predicted_delta_pe + 0).toFixed(2)} on the line, ` +
      `predicted ROE ${option.predicted_roe_pct.toFixed(0)}% (${option.predicted_roe_band}). ` +
      "Help me think through what I should actually DO this week to make it real — " +
      "not just sign up for it.";
    pieCoachPreseed.set(seed);
    navigate('/mirror/coach');
  };

  /** "Commit & coach me through it" — one-click handoff that saves
   *  the commitment AND drops the user into a Coach session pre-seeded
   *  to build a 7-day execution plan for the action they just picked.
   *  Stitches the commit→coach moment into a single tap so users don't
   *  drop the ball between "I picked one" and "I actually did it."
   */
  const onCommitAndCoach = async (option) => {
    if (committingId) return;
    setCommittingId(option.id);
    try {
      await pieApi.createCommitment(option.id);
      const seed =
        `I just committed to: "${option.label}". ` +
        `My predicted ROE is ${option.predicted_roe_pct.toFixed(0)}% (${option.predicted_roe_band}), ` +
        `effort ~${option.effort_minutes >= 60 ? `${(option.effort_minutes / 60).toFixed(1)} hours` : `${option.effort_minutes} min`}. ` +
        "Help me build a realistic 7-day execution plan — what to do today, " +
        "what to do mid-week, and how I'll know it's actually landing. " +
        "Be specific. Don't let me wave this away.";
      pieCoachPreseed.set(seed);
      navigate('/mirror/coach');
    } catch (e) {
      logger.error('commit-and-coach failed', e);
      setErr(e?.response?.data?.detail || 'Could not save commitment');
      setCommittingId(null);
    }
  };

  if (loading) return <PIELayout><Loading /></PIELayout>;
  if (err) return <PIELayout>{isUnseededError(err) ? <PIEUnseededEmptyState /> : <div className="pie-card" style={{ color: 'var(--pie-danger)' }}>{err}</div>}</PIELayout>;
  if (!data) return null;

  const top = data.action_options.find((a) => a.id === data.top_priority_id) || data.action_options[0];
  const others = data.action_options.filter((a) => a.id !== top.id);

  return (
    <PIELayout>
      <div className="pie-fade-in" data-testid="pie-improve">
        <section className="pie-hero-center">
          <span className="pie-eyebrow">
            <Target size={12} /> What to improve
          </span>
          <h1 className="pie-hero-h1">
            Here&apos;s the <span className="pie-accent-text">one move</span>.
          </h1>
          <p className="pie-hero-sub" data-testid="pie-improve-diagnosis">
            {data.diagnosis}
          </p>
          {data.narrative_source === 'fallback' && <PIEFallbackBadge />}
        </section>

        {/* TOP PRIORITY HERO ─────────────────────── */}
        <div
          className="pie-card pie-card-elevated"
          style={{ marginBottom: 36, position: 'relative', overflow: 'hidden' }}
          data-testid="pie-top-priority"
        >
          <div
            style={{
              position: 'absolute', inset: '0 0 auto 0', height: 4,
              background: 'linear-gradient(90deg, var(--pie-accent) 0%, var(--pie-text) 100%)',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Award size={14} style={{ color: 'var(--pie-accent)' }} />
            <span className="pie-eyebrow" style={{ background: 'transparent', padding: 0, color: 'var(--pie-accent)' }}>
              Your top priority this week
            </span>
          </div>
          <h2 className="pie-display-md" style={{ marginBottom: 16, lineHeight: 1.2 }}>
            {top.label}
          </h2>
          <p style={{ fontSize: 15, color: 'var(--pie-text-secondary)', lineHeight: 1.65, marginBottom: 22 }}>
            {data.top_priority_why}
          </p>
          {top.prior_pulse_summary && (
            <div style={{ marginBottom: 22 }}>
              <PriorPulseHint summary={top.prior_pulse_summary} />
            </div>
          )}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 16,
              marginBottom: 24,
              paddingTop: 20,
              borderTop: '1px solid var(--pie-border)',
            }}
          >
            <Stat label="Effort" value={formatEffort(top.effort_minutes)} />
            <Stat
              label="ΔPE"
              value={<Perf value={top.predicted_delta_pe} kind="delta01" digits={1} />}
              accent
              tooltip="Your effectiveness lift, in percentage points. If your Personal Effectiveness is 76% today and ΔPE is +2.2 pts, you'd land at about 78.2%."
            />
            <Stat
              label="Predicted ROE"
              value={<><Perf value={top.predicted_roe_pct} kind="pct100" /> · {top.predicted_roe_band}</>}
              tooltip="Predicted Return on Expectation — how likely this is to drive a meaningful, visible lift in YOUR performance (capability + motivation), recognised by you and others. Bands: Low / Moderate / High."
              extra={<PIECalibrationBadge calibration={top.calibration} compact />}
            />
            {top.top_kpi && (
              <Stat
                label={top.top_kpi.name}
                value={`$${top.top_kpi.value_per_employee.toLocaleString()} / yr`}
                tooltip="Annual dollar value of productivity uplift YOU'RE expected to add by doing this. Same metric your org tracks at the team level — this is your slice."
              />
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="pie-btn pie-btn-primary"
              onClick={() => onCommitAndCoach(top)}
              disabled={committingId === top.id}
              data-testid="pie-improve-commit-and-coach-top"
              title="Save this commitment AND open the Coach to build a 7-day plan"
            >
              <Compass size={14} />
              {committingId === top.id ? 'Saving…' : 'Commit & coach me through it'}
              <ArrowRight size={14} />
            </button>
            <button
              type="button"
              className="pie-btn pie-btn-ghost"
              onClick={() => onCommit(top.id)}
              disabled={committingId === top.id}
              data-testid="pie-improve-commit-top"
            >
              <CheckCircle2 size={14} />
              {committingId === top.id ? 'Saving…' : 'Just commit'}
            </button>
            <Link to="/mirror/simulate" className="pie-btn pie-btn-ghost" data-testid="pie-improve-explore">
              <Sparkles size={14} /> Explore other options
            </Link>
            <button
              type="button"
              className="pie-btn pie-btn-ghost"
              onClick={() => onTalkOver(top)}
              data-testid="pie-improve-talk-over"
            >
              <MessageCircleHeart size={14} /> Talk it over
            </button>
          </div>
        </div>

        {/* OTHER OPTIONS ─────────────────────────── */}
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>
          Other options, ranked by impact-per-effort
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="pie-other-options">
          {others.map((opt) => (
            <OptionRow
              key={opt.id}
              opt={opt}
              onCommit={() => onCommit(opt.id)}
              onTalkOver={() => onTalkOver(opt)}
              onCommitAndCoach={() => onCommitAndCoach(opt)}
              committing={committingId === opt.id}
              disabled={!!committingId && committingId !== opt.id}
            />
          ))}
        </div>
      </div>
    </PIELayout>
  );
}

function Stat({ label, value, accent = false, tooltip = null, extra = null }) {
  return (
    <div>
      <div
        className={tooltip ? 'pie-stat-label pie-stat-label-with-tip' : 'pie-stat-label'}
        data-tooltip={tooltip || undefined}
      >
        <span>{label}</span>
        {tooltip && (
          <span className="pie-stat-info" aria-hidden>
            <Info size={10} />
          </span>
        )}
      </div>
      <div
        className="pie-mono"
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: accent ? 'var(--pie-accent)' : 'var(--pie-text)',
        }}
      >
        {value}
      </div>
      {extra && <div style={{ marginTop: 6 }}>{extra}</div>}
    </div>
  );
}

function OptionRow({ opt, onCommit, onTalkOver, onCommitAndCoach, committing, disabled }) {
  return (
    <div
      className="pie-card"
      style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 18, alignItems: 'center' }}
      data-testid={`pie-option-${opt.id}`}
    >
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--pie-text)', marginBottom: 4 }}>
          {opt.label}
        </div>
        {opt.coach_blurb && (
          <div style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--pie-text-secondary)', marginBottom: 12 }}>
            “{opt.coach_blurb}”
          </div>
        )}
        {opt.prior_pulse_summary && <PriorPulseHint summary={opt.prior_pulse_summary} />}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: 'var(--pie-text-muted)' }}>
          <span>~{formatEffort(opt.effort_minutes)}</span>
          <span>·</span>
          <span
            className="pie-inline-tip"
            data-tooltip="Your effectiveness lift, in percentage points — how many points your Personal Effectiveness score will climb."
            tabIndex={0}
          >
            ΔPE{' '}
            <span className="pie-mono pie-delta-positive">
              <Perf value={opt.predicted_delta_pe} kind="delta01" digits={1} />
            </span>
          </span>
          <span>·</span>
          <span
            className="pie-inline-tip"
            data-tooltip="Predicted Return on Expectation — how likely this is to drive a real, visible lift in YOUR performance. Bands: Low / Moderate / High."
            tabIndex={0}
          >
            ROE{' '}
            <span className="pie-mono"><Perf value={opt.predicted_roe_pct} kind="pct100" /></span>{' '}
            <span style={{ color: 'var(--pie-text-muted)' }}>({opt.predicted_roe_band})</span>
          </span>
          {opt.calibration && opt.calibration.applied && (
            <>
              <span>·</span>
              <PIECalibrationBadge calibration={opt.calibration} compact />
            </>
          )}
          {opt.top_kpi && (
            <>
              <span>·</span>
              <span
                className="pie-inline-tip"
                data-tooltip="Annual dollar value of productivity uplift you'd personally add by doing this. Same metric your org tracks — this is your slice."
                tabIndex={0}
              >
                {opt.top_kpi.name}{' '}
                <span className="pie-mono" style={{ color: 'var(--pie-text)', fontWeight: 600 }}>
                  ${opt.top_kpi.value_per_employee.toLocaleString()}/yr
                </span>
              </span>
            </>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch', minWidth: 200 }}>
        <button
          type="button"
          className="pie-btn pie-btn-primary pie-btn-commit-coach"
          onClick={onCommitAndCoach}
          disabled={committing || disabled}
          data-testid={`pie-option-commit-coach-${opt.id}`}
          style={{ whiteSpace: 'nowrap', justifyContent: 'center' }}
          title="Save this commitment AND open the Coach to build a 7-day plan"
        >
          <Compass size={13} /> {committing ? 'Saving…' : 'Commit & coach me'}
        </button>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            className="pie-btn pie-btn-ghost pie-btn-coach-this"
            onClick={onTalkOver}
            disabled={disabled}
            data-testid={`pie-option-coach-${opt.id}`}
            style={{ whiteSpace: 'nowrap', justifyContent: 'center', flex: 1, padding: '6px 8px', fontSize: 12 }}
            title="Open the AI Coach pre-seeded with this action"
          >
            <MessageCircleHeart size={12} /> Coach this
          </button>
          <button
            type="button"
            className="pie-btn pie-btn-ghost"
            onClick={onCommit}
            disabled={committing || disabled}
            data-testid={`pie-option-commit-${opt.id}`}
            style={{ whiteSpace: 'nowrap', justifyContent: 'center', flex: 1, padding: '6px 8px', fontSize: 12 }}
          >
            Just commit <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="pie-fade-in">
      <section className="pie-hero-center" style={{ marginBottom: 28 }}>
        <PIESkeleton variant="chip" width={140} style={{ margin: '0 auto 16px' }} />
        <PIESkeleton variant="line-xl" width="62%" style={{ margin: '0 auto 14px' }} />
        <PIESkeletonParagraph lines={2} lastWidth="56%" />
      </section>
      <PIESkeleton variant="card" style={{ height: 220, marginBottom: 16 }} />
      <PIESkeleton variant="card" style={{ height: 120, marginBottom: 12 }} />
      <PIESkeleton variant="card" style={{ height: 120, marginBottom: 12 }} />
      <div style={{ marginTop: 18, fontSize: 13, color: 'var(--pie-text-muted)', textAlign: 'center' }}>
        Coach is reading your numbers…
      </div>
    </div>
  );
}

function formatEffort(mins) {
  if (mins < 60) return `${mins} min`;
  const hours = mins / 60;
  if (hours < 8) return `${hours.toFixed(1)} hours`;
  return `${Math.round(hours)} hours total`;
}


/**
 * PriorPulseHint — soft "you've been here before" reminder rendered
 * inside an action card when the learner has previously committed to
 * THIS action and logged at least one weekly pulse against it. Anchors
 * the user in their own pattern before re-committing — closes the
 * validation→improve loop in the opposite direction from the Validate
 * page's "in your own words" strip.
 *
 * Rendering rules
 *   • If `had_friction` and `top_friction_tags` is non-empty → amber
 *     warning tone, names the tags. Most actionable signal.
 *   • Else if last application was high (≥4) → green "you've shown
 *     up" reinforcement. Re-commit with confidence.
 *   • Else → neutral "you've tried this before" footer. No pressure.
 */
function PriorPulseHint({ summary }) {
  const tags = summary.top_friction_tags || [];
  const hadFriction = !!summary.had_friction && tags.length > 0;
  const stuckLastTime = (summary.last_application || 0) >= 4;

  let tone, icon, text;
  if (hadFriction) {
    tone = { bg: 'rgba(245, 158, 11, 0.10)', border: 'rgba(245, 158, 11, 0.35)', color: '#92400E' };
    icon = '⚠';
    const tagPhrase = tags.length === 1 ? tags[0] : `${tags[0]} & ${tags[1]}`;
    text = (
      <>
        Last time you committed to this, you logged friction on <strong>{tagPhrase}</strong>.
        Worth designing around that this round.
      </>
    );
  } else if (stuckLastTime) {
    tone = { bg: 'rgba(16, 185, 129, 0.08)', border: 'rgba(16, 185, 129, 0.30)', color: '#065F46' };
    icon = '✓';
    text = (
      <>
        You showed up for this last time (applied <strong>{summary.last_application}/5</strong>).
        Re-commit with confidence.
      </>
    );
  } else {
    tone = { bg: 'rgba(100, 116, 139, 0.08)', border: 'rgba(100, 116, 139, 0.25)', color: 'var(--pie-text-secondary)' };
    icon = '•';
    text = <>You've tried this before — {summary.pulses_logged} pulse{summary.pulses_logged === 1 ? '' : 's'} on the books.</>;
  }

  return (
    <div
      data-testid={`pie-prior-pulse-hint-${summary.last_week_iso}`}
      style={{
        marginBottom: 12, padding: '8px 12px', borderRadius: 8,
        background: tone.bg, border: `1px solid ${tone.border}`,
        color: tone.color, fontSize: 12, lineHeight: 1.55,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <span style={{ flexShrink: 0, fontWeight: 700 }} aria-hidden>{icon}</span>
        <span>{text}</span>
      </div>
      {summary.timeline && summary.timeline.length > 1 && (
        <PulseTimeline timeline={summary.timeline} />
      )}
    </div>
  );
}

/**
 * PulseTimeline — left-to-right dot row of the trailing N pulses on
 * THIS action. Reads "you've succeeded twice and just hit friction
 * once" rather than the warning above implying the action has always
 * been a problem. Helps avoid over-reading a single bad week.
 */
function PulseTimeline({ timeline }) {
  const STATUS = {
    stuck:    { icon: '✓', color: '#065F46', bg: 'rgba(16, 185, 129, 0.18)', label: 'stuck' },
    friction: { icon: '⚠', color: '#92400E', bg: 'rgba(245, 158, 11, 0.22)', label: 'friction' },
    missed:   { icon: '·', color: '#475569', bg: 'rgba(100, 116, 139, 0.18)', label: 'low application' },
  };
  return (
    <div
      data-testid="pie-pulse-timeline"
      style={{
        marginTop: 8, paddingTop: 8,
        borderTop: '1px dashed currentColor',
        opacity: 0.85,
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.7, marginRight: 2 }}>
        Recent
      </span>
      {timeline.map((p, idx) => {
        const t = STATUS[p.status] || STATUS.missed;
        return (
          <span
            key={`${p.week_iso}-${idx}`}
            title={`${p.week_iso} — ${t.label} (applied ${p.applied}/5)`}
            data-testid={`pie-pulse-timeline-dot-${p.week_iso}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 8px', borderRadius: 999,
              background: t.bg, color: t.color,
              fontSize: 10, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            <span style={{ fontWeight: 700 }} aria-hidden>{t.icon}</span>
            {p.week_iso}
          </span>
        );
      })}
    </div>
  );
}
